import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar, MapPin, Palette } from 'lucide-react';
import type { Event, EventFormValues, EventColor, EventType } from '../types';
import {
  EVENT_TYPE_LABELS,
  EVENT_STATUS_LABELS,
  EVENT_COLOR_MAP,
} from '../types';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  title:            z.string().min(2, 'Mínimo 2 caracteres').max(200, 'Máximo 200 caracteres'),
  description:      z.string().max(2000).optional().default(''),
  type:             z.enum(['reuniao', 'evento', 'atividade', 'panfletagem', 'outro']),
  status:           z.enum(['scheduled', 'ongoing', 'completed', 'cancelled']).default('scheduled'),
  location_name:    z.string().max(200).optional().default(''),
  location_address: z.string().max(400).optional().default(''),
  starts_at:        z.string().min(1, 'Informe a data/hora de início'),
  ends_at:          z.string().optional().default(''),
  notes:            z.string().max(4000).optional().default(''),
  color:            z.enum(['cyan', 'blue', 'emerald', 'amber', 'rose', 'purple']).default('cyan'),
}).refine((d) => {
  if (!d.ends_at) return true;
  return new Date(d.ends_at) > new Date(d.starts_at);
}, { message: 'Término deve ser após o início', path: ['ends_at'] });

// ── Props ──────────────────────────────────────────────────────────────────────

interface EventFormProps {
  initial?: Event;
  onSubmit: (values: EventFormValues) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// ── Color picker ───────────────────────────────────────────────────────────────

const COLORS: EventColor[] = ['cyan', 'blue', 'emerald', 'amber', 'rose', 'purple'];
const COLOR_LABELS: Record<EventColor, string> = {
  cyan: 'Ciano', blue: 'Azul', emerald: 'Verde', amber: 'Âmbar', rose: 'Rosa', purple: 'Roxo',
};

// ── Component ─────────────────────────────────────────────────────────────────

export function EventForm({ initial, onSubmit, onCancel, isSubmitting }: EventFormProps) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<EventFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title:            '',
      description:      '',
      type:             'evento',
      status:           'scheduled',
      location_name:    '',
      location_address: '',
      starts_at:        '',
      ends_at:          '',
      notes:            '',
      color:            'cyan',
    },
  });

  useEffect(() => {
    if (initial) {
      reset({
        title:            initial.title,
        description:      initial.description ?? '',
        type:             initial.type,
        status:           initial.status,
        location_name:    initial.location_name ?? '',
        location_address: initial.location_address ?? '',
        starts_at:        initial.starts_at
                            ? toLocalDatetimeValue(initial.starts_at)
                            : '',
        ends_at:          initial.ends_at
                            ? toLocalDatetimeValue(initial.ends_at)
                            : '',
        notes:            initial.notes ?? '',
        color:            initial.color,
      });
    }
  }, [initial, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

      {/* Título */}
      <div className="space-y-1.5">
        <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
          Título <span className="text-danger">*</span>
        </label>
        <input
          {...register('title')}
          placeholder="Ex: Reunião com lideranças do bairro..."
          className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />
        {errors.title && (
          <p className="text-xs text-danger">{errors.title.message}</p>
        )}
      </div>

      {/* Tipo + Cor */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
            Tipo
          </label>
          <select
            {...register('type')}
            className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-accent/50 transition-colors"
          >
            {(Object.keys(EVENT_TYPE_LABELS) as EventType[]).map((t) => (
              <option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
            <span className="flex items-center gap-1.5"><Palette className="h-3 w-3" /> Cor</span>
          </label>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={COLOR_LABELS[c]}
                    onClick={() => field.onChange(c)}
                    className={`h-7 w-7 rounded-full transition-all ${EVENT_COLOR_MAP[c].dot} ${
                      field.value === c
                        ? 'ring-2 ring-offset-2 ring-offset-midnight-2 ring-white scale-110'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                  />
                ))}
              </div>
            )}
          />
        </div>
      </div>

      {/* Datas */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
            <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Início <span className="text-danger">*</span></span>
          </label>
          <input
            type="datetime-local"
            {...register('starts_at')}
            className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-accent/50 transition-colors"
          />
          {errors.starts_at && (
            <p className="text-xs text-danger">{errors.starts_at.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
            Término
          </label>
          <input
            type="datetime-local"
            {...register('ends_at')}
            className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-accent/50 transition-colors"
          />
          {errors.ends_at && (
            <p className="text-xs text-danger">{errors.ends_at.message}</p>
          )}
        </div>
      </div>

      {/* Local */}
      <div className="space-y-1.5">
        <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
          <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Local</span>
        </label>
        <input
          {...register('location_name')}
          placeholder="Nome do local (ex: Centro Comunitário João Paulo)"
          className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />
        <input
          {...register('location_address')}
          placeholder="Endereço completo"
          className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />
      </div>

      {/* Descrição */}
      <div className="space-y-1.5">
        <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
          Descrição
        </label>
        <textarea
          {...register('description')}
          rows={3}
          placeholder="Contexto, pauta, instruções..."
          className="w-full resize-none rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />
      </div>

      {/* Status (visível ao editar) */}
      {initial && (
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
            Status
          </label>
          <select
            {...register('status')}
            className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink focus:outline-none focus:border-accent/50 transition-colors"
          >
            {Object.entries(EVENT_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      )}

      {/* Notas internas */}
      <div className="space-y-1.5">
        <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wider">
          Notas internas
        </label>
        <textarea
          {...register('notes')}
          rows={2}
          placeholder="Informações de acesso, materiais necessários..."
          className="w-full resize-none rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />
      </div>

      {/* Rodapé */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-xl btn-ghost px-5 py-2.5 text-sm font-semibold"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-xl btn-accent px-5 py-2.5 text-sm font-semibold disabled:opacity-60"
        >
          {isSubmitting ? 'Salvando…' : initial ? 'Salvar alterações' : 'Criar evento'}
        </button>
      </div>
    </form>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Convert ISO UTC string to local datetime-local input value */
function toLocalDatetimeValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
